import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { isEmpty, keyBy, omit, toArray, toPlainObject, uniqBy, values } from 'lodash';

import { PATH } from '../../../constants/path';
import { CONTEXT_ROOT } from '../../../constants';
import {
    SORT_PARTNER_ON_NAME,
    SORT_PARTNER_ON_CASHBACK,
    SORT_PARTNER_ON_ORDER,
    SORT_PARTNER_ON_CREATION_DATE,
    SORT_PARTNER_ON_NB_COUPON
} from '../../../services/filter/constants';

import {
    categoryDisplayOrder,
    filterOnCategory,
    filterOnCategoryEbon,
    filterOnFirstLetter,
    sortOnName,
    sortOnNbCoupon,
    sortOnOrder,
    sortOnOrderOffline
} from '../../../services/partner/selector';
import {
    hasCashback,
    hasCouponsOnly,
    hasDisplayableCoupon,
    hasOffline,
    hasCashbackOffline,
    hasEbon,
    hasEbonCashback,
    convertPartnerNameForUrl
} from '../../../services/partner/utils';
import {
    OFFER_TYPE_URL_PARAM,
    OFFER_TYPE_ONLINE,
    OFFER_TYPE_EBON,
    OFFER_TYPE_COUPON,
    OFFER_TYPE_BILLETTERIE
} from '../../../services/directory/constants';
import { toBilletteriePartners } from '../../../services/billetterie/utils';

import { getQueryParams } from '../../../utils/routeUtils';

import { formatCategoryNameForUrl } from '../../../services/category/utils';

import HeadComponent from '../../HeadComponent';
import Breadcrumb from '../../breadcrumb/Breadcrumb';
import { withTemplate } from '../../template/withTemplate';
import { CategoryDirectoryTemplate } from '../../template/CategoryDirectoryTemplate';
import ABCBookComponent from './ABCBookComponent';
import FilterComponent from './filter/FilterComponent';
import GridDirectoryComponent from './GridDirectoryComponent';
import HowItWorksComponent from '../../HowItWorksComponent';

import './DirectoryPage.scss';

// Un "bloc" = un partenaire décoré du type d'offre qu'il représente dans la
// grille mixte ; un même partenaire peut donner plusieurs blocs (en ligne,
// en magasin, bon d'achat, billetterie, ou codes promo seuls).
// GridDirectoryComponent lit ces flags par bloc.
// La liste est triée par popularité de son référentiel et chaque bloc reçoit
// son rang (blockRank) : les champs d'ordre des référentiels classique
// (turnoverEbcOrder) et ebon (order) ne sont pas comparables entre eux, le
// tri "Les + populaires" mixte entrelace donc par rang. Sans comparateur, la
// liste garde son ordre (mur billetterie, déjà trié par popularité).
const toBlocks = (list, comparator, blockIsOffline, blockIsEbon, blockIsBilletterie = false) =>
    (comparator ? [...list].sort(comparator) : list).map((partner, index) => ({
        ...partner,
        blockIsOffline,
        blockIsEbon,
        blockIsBilletterie,
        blockRank: index
    }));

const sortEbonOnOrder = (p1, p2) => (p1.order || 0) - (p2.order || 0);

// Remise affichée par le bloc, selon son type (les partenaires ebon ont une
// structure de remise différente).
const blockDiscount = block =>
    block.blockIsBilletterie
        ? {
              // Le bloc affiche un prix : seul le cashback du bon d'achat est comparable
              percent: hasEbonCashback(block) ? block.ebonDiscount.cashback.percent.value : 0,
              fixed: 0
          }
        : block.blockIsEbon
        ? {
              percent: block.ebonDiscount.cashback.percent.value || 0,
              fixed: block.ebonDiscount.cashback.fixed.value || 0
          }
        : {
              // La remise peut être absente (marchand à codes promo sans
              // cashback) : le bloc est alors trié en fin de liste.
              percent:
                  (
                      (block.blockIsOffline
                          ? block.mostDisplayableDiscountOffline
                          : block.mostDisplayableDiscountOnline) || {}
                  ).percent || 0,
              fixed:
                  (
                      (block.blockIsOffline
                          ? block.mostDisplayableDiscountOffline
                          : block.mostDisplayableDiscountOnline) || {}
                  ).fixed || 0
          };

// Ordre de popularité d'un bloc = son rang dans son référentiel (voir toBlocks).
const blockOrder = block => block.blockRank;

// Date de création en secondes : /api/partner l'expose en secondes, /ebon/partner en millisecondes
const blockCreationDate = block =>
    block.blockIsEbon ? (block.creationDate || 0) / 1000 : block.creationDate || 0;

/**
 * Annuaire tous types d'offres confondus : une seule grille mixant les offres
 * en ligne, en magasin et en bon d'achat. Trois modes selon la route :
 * - par catégorie : /cashback/categorie/{categoryName}-{categoryId}
 * - alphabétique : /cashback/alphabetique/{letter}
 * - tous les marchands (défaut) : /cashback/les-plus-populaires
 */
export class AllOffersDirectoryPage extends React.Component {
    sortBlocks = (sorter, blocks) => {
        if (!sorter) {
            return blocks;
        }
        switch (sorter.type) {
            case SORT_PARTNER_ON_NAME:
                return blocks.sort(sortOnName);
            case SORT_PARTNER_ON_CASHBACK:
                return blocks.sort((b1, b2) => {
                    const d1 = blockDiscount(b1);
                    const d2 = blockDiscount(b2);
                    if (d1.percent !== d2.percent) {
                        return d2.percent - d1.percent;
                    }
                    if (d1.fixed !== d2.fixed) {
                        return d2.fixed - d1.fixed;
                    }
                    return blockOrder(b1) - blockOrder(b2);
                });
            case SORT_PARTNER_ON_ORDER:
                // « Les + populaires » : les marchands avec un ordre imposé pour la
                // catégorie courante (CATEGORY_DISPLAY_ORDER, tout type d'offre
                // confondu) passent devant, dans cet ordre — les blocs d'un même
                // marchand épinglé restent groupés ; le reste garde l'entrelacement
                // par rang des référentiels. Hors catégorie, aucun épinglage.
                return blocks.sort((b1, b2) => {
                    const c1 = categoryDisplayOrder(b1, this.props.categoryId);
                    const c2 = categoryDisplayOrder(b2, this.props.categoryId);
                    if (c1 != null || c2 != null) {
                        if (c1 == null) {
                            return 1;
                        }
                        if (c2 == null) {
                            return -1;
                        }
                        if (c1 !== c2) {
                            return c1 - c2;
                        }
                    }
                    return blockOrder(b1) - blockOrder(b2);
                });
            case SORT_PARTNER_ON_CREATION_DATE:
                return blocks.sort((b1, b2) => blockCreationDate(b2) - blockCreationDate(b1));
            // Proposé par le select en mode "Code de réduction"
            case SORT_PARTNER_ON_NB_COUPON:
                return blocks.sort(sortOnNbCoupon);
            default:
                return blocks;
        }
    };

    render() {
        const {
            match,
            location,
            letter,
            categoryId,
            isCodePromo,
            category,
            categories,
            categoriesFetching,
            blocks,
            uniquePartners,
            sorter,
            fetching,
            i18nAZ,
            i18nAllOffers
        } = this.props;

        const categorySlug = `${formatCategoryNameForUrl(category.label)}-${category.id}`;

        // Gardes du mode catégorie, une fois le référentiel de catégories chargé.
        if (categoryId && !categoriesFetching && values(categories).length > 0) {
            // Catégorie inexistante : page 404.
            if (isEmpty(category)) {
                return <Redirect to={PATH.NOT_FOUND_PATH} />;
            }
            // Slug non canonique : redirection vers l'url propre (le tri en
            // query string est conservé).
            if (match.params.categoryName !== formatCategoryNameForUrl(category.label)) {
                return (
                    <Redirect
                        to={{
                            pathname: `${PATH.CASHBACK_CATEGORY_PATH}${categorySlug}`,
                            search: location.search
                        }}
                    />
                );
            }
        }

        const canonicalLink = letter
            ? `${CONTEXT_ROOT}${PATH.CASHBACK_ALPHA_PATH}${letter}`
            : categoryId
            ? `${CONTEXT_ROOT}${PATH.CASHBACK_CATEGORY_PATH}${categorySlug}`
            : `${CONTEXT_ROOT}${PATH.CASHBACK_MOST_POPULAR}`;
        const pageTitle = letter
            ? i18nAZ.PAGE_TITLE
            : categoryId
            ? category.label
            : i18nAllOffers.TITLE;
        const blockList = this.sortBlocks(sorter, blocks);

        return (
            <div>
                <HeadComponent
                    title={
                        letter ? i18nAZ.TITLE : categoryId ? category.label : i18nAllOffers.TITLE
                    }
                    description={
                        letter
                            ? i18nAZ.DESCRIPTION
                            : categoryId
                            ? category.label
                            : i18nAllOffers.DESCRIPTION
                    }
                    canonicalLink={canonicalLink}
                />
                <Breadcrumb
                    defaultRoute
                    routes={[
                        {
                            label: letter
                                ? i18nAZ.BREADCRUMB
                                : categoryId
                                ? category.label
                                : i18nAllOffers.BREADCRUMB,
                            route: match.url
                        }
                    ]}
                    cssClass="conteneur"
                />
                <div className="flex-container-v">
                    <main role="main" className="conteneur conteneur-mobile" id="p-annuaire">
                        <div className="directory-head">
                            <div>
                                <h1>{pageTitle}</h1>
                            </div>
                        </div>
                        <FilterComponent
                            partners={uniquePartners}
                            categoryId={category && category.id ? Number(category.id) : -1}
                            categories={values(categories)}
                            isAllOffers
                            isCodePromo={isCodePromo}
                        />
                        <ABCBookComponent letter={letter || ''} isCodePromo={false} isAllOffers />
                        {/* Pas de badge de type en mode "Code de réduction" :
                            tous les blocs sont du même type. */}
                        <GridDirectoryComponent
                            partners={blockList}
                            fetching={fetching}
                            showType={!isCodePromo}
                            isCodePromo={isCodePromo}
                        />
                    </main>
                    <HowItWorksComponent
                        topShadow
                        showTitle
                        cssClass="conteneur conteneur-mobile"
                    />
                </div>
            </div>
        );
    }
}

AllOffersDirectoryPage.propTypes = {
    letter: PropTypes.string,
    categoryId: PropTypes.number,
    isCodePromo: PropTypes.bool,
    category: PropTypes.object.isRequired,
    categories: PropTypes.object.isRequired,
    categoriesFetching: PropTypes.bool.isRequired,
    blocks: PropTypes.array.isRequired,
    uniquePartners: PropTypes.array.isRequired,
    sorter: PropTypes.object,
    fetching: PropTypes.bool.isRequired,
    i18nAZ: PropTypes.object.isRequired,
    i18nAllOffers: PropTypes.object.isRequired
};

const mapStateToProps = (state, ownProps) => {
    const letter = ownProps.match.params.letter || null;
    const categoryId = Number(ownProps.match.params.categoryId) || null;

    let partners = toArray(state.partner.partners);
    let billetteriePartners = toBilletteriePartners(
        state.billetterie.venues,
        state.partner.partners,
        state.ebon.ebonPartners
    );
    // Un marchand billetterie a un bloc billetterie à la place de son bloc bon d'achat
    const billetteriePartnersById = keyBy(billetteriePartners, 'id');
    let ebonPartners = toArray(state.ebon.ebonPartners).filter(
        p => hasEbon(p) && hasEbonCashback(p) && !billetteriePartnersById[p.id]
    );

    if (letter) {
        partners = partners.filter(filterOnFirstLetter(letter));
        ebonPartners = ebonPartners.filter(filterOnFirstLetter(letter));
        billetteriePartners = billetteriePartners.filter(filterOnFirstLetter(letter));
    } else if (categoryId) {
        partners = partners.filter(filterOnCategory(categoryId));
        ebonPartners = ebonPartners.filter(filterOnCategoryEbon(categoryId));
        billetteriePartners = billetteriePartners.filter(filterOnCategory(categoryId));
    }
    // Ni lettre ni catégorie (les-plus-populaires) : tous les marchands.

    // Filtre par type d'offre (?offre=...) : un seul type de blocs, ou tous.
    // "Code de réduction" remonte les marchands ayant au moins un code promo.
    const offerType = getQueryParams(ownProps.location.search)[OFFER_TYPE_URL_PARAM] || null;
    const isCodePromo = offerType === OFFER_TYPE_COUPON;

    // Codes promo seuls (règle de la fiche "Offres") : ebon cherché dans le référentiel non filtré
    const allEbonPartnersById = keyBy(state.ebon.ebonPartners, 'id');
    const isCouponsOnly = p => hasCouponsOnly(p, allEbonPartnersById[p.id]);
    const withCouponsOnlyFlag = block => ({ ...block, blockIsCouponsOnly: isCouponsOnly(block) });

    const blocks = isCodePromo
        ? toBlocks(partners.filter(hasDisplayableCoupon), sortOnOrder, false, false).map(
              withCouponsOnlyFlag
          )
        : offerType === OFFER_TYPE_ONLINE
        ? toBlocks(partners.filter(hasCashback), sortOnOrder, false, false)
        : offerType === OFFER_TYPE_EBON
        ? toBlocks(ebonPartners, sortEbonOnOrder, false, true)
        : offerType === OFFER_TYPE_BILLETTERIE
        ? toBlocks(billetteriePartners, null, false, false, true)
        : [
              // Un marchand à codes promo seuls n'a pas d'autre bloc : classé parmi les en ligne
              ...toBlocks(
                  partners.filter(p => hasCashback(p) || isCouponsOnly(p)),
                  sortOnOrder,
                  false,
                  false
              ).map(withCouponsOnlyFlag),
              ...toBlocks(
                  partners.filter(p => hasOffline(p) && hasCashbackOffline(p)),
                  sortOnOrderOffline,
                  true,
                  false
              ),
              ...toBlocks(ebonPartners, sortEbonOnOrder, false, true),
              ...toBlocks(billetteriePartners, null, false, false, true)
          ];

    return {
        letter,
        categoryId,
        isCodePromo,
        category: (categoryId && state.category.categories[categoryId]) || {},
        categories: toPlainObject(omit(state.category.categories, 'loading')),
        categoriesFetching: state.category.fetching,
        blocks,
        // Compteur : un partenaire présent via plusieurs blocs (ex. Carrefour
        // en ligne + bon d'achat) ne compte qu'une fois. Les référentiels
        // classique et ebon étant distincts, on dédoublonne sur le nom normalisé.
        uniquePartners: uniqBy(blocks, block => convertPartnerNameForUrl(block.name)),
        sorter: (state.filters.directory || {}).sorter,
        fetching:
            state.partner.fetching || state.ebon.ebonPartnersFetching || state.billetterie.fetching,
        i18nAZ: state.i18n.page.DIRECTORY.A_Z,
        i18nAllOffers: state.i18n.page.DIRECTORY.ALL_OFFERS
    };
};

export default withTemplate(
    connect(mapStateToProps)(AllOffersDirectoryPage),
    CategoryDirectoryTemplate
);



// WEBPACK FOOTER //
// ./src/components/pages/directory/AllOffersDirectoryPage.js